import { useEffect, useReducer, useState } from 'react'

import { faFilter, faTableList } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Badge, Box, IconButton, Paper, Typography } from '@material-ui/core'
import { Tooltip } from '@scoir/tooltips'
import { Link } from 'react-router-dom'

import api from 'common/api'
import useTitle from 'common/use-title'
import FillViewHeight from 'components/FillViewHeight'
import { useHistory } from 'components/ReactRouterProvider/context'
import { useAppSelector } from 'store'

import { getFollowerClasses, getHandraiserClasses, getHistoricFollowerYears } from '../ExploreTable/gradYears'
import useTargetHighschoolsExplore from '../ExploreTable/useTargetHighschoolsExplore'
import HighschoolPreviewDrawer from '../Preview/HighschoolPreviewDrawer'
import FilterDrawer from '../components/FilterDrawer'
import PremiumActionsWrapper from '../components/PremiumPreview/PremiumActions'
import ViewsButton from '../components/Views/ViewsButton'
import { useViews } from '../components/Views/useViews'
import getFilterCount from '../filter-state-count'
import { createAPIFiltersFromUIFilters, createURLSeachParamsFromAPIFilters } from '../filter-state-to-api-query'
import { decodeExploreMapStateParams, encodeExploreMapState } from '../filter-state-to-map-url-params'
import { encodeToURLParams } from '../filter-state-to-url-params'
import { UIFilterConverters } from '../filter-state-to-url-params'
import RawGoogleMap, { withGoogleMapsApi } from './GoogleMap'
import { cluster, highSchool as googleMapHighSchool } from './GoogleMap/types'
import PlaceFinder from './PlaceFinder'

const GOOGLE_MAPS_KEY = 'AIzaSyBrUOmema_7ysyJal6EUNqqzGUlAljiOjw'

const GoogleMap = withGoogleMapsApi(GOOGLE_MAPS_KEY)(RawGoogleMap)

type APIGeoItem = {
  id: string
  lat: number
  lon: number
  count: number
  value: number
}

type APIHighSchoolItem = {
  id: string
  name: string
  city: string
  state: string
  active?: boolean
  seniorClassSize?: number
  nextVisitDate?: string
  lat: number
  lng: number
}
type APIResult = {
  clusters: APIGeoItem[]
  records: APIHighSchoolItem[]
}

type highSchool = googleMapHighSchool & {
  city: string
  state: string
  seniorClassSize?: number
  nextVisitDate?: string
}

type State = {
  clusters: cluster[]
  highSchools: highSchool[]
  isLoading: boolean
  center: { lat: number; lng: number }
  zoom: number
  selectedHighschool?: highSchool
}

const initialState: Omit<State, 'center' | 'zoom'> = {
  clusters: [],
  highSchools: [],
  isLoading: false,
}

type Action =
  | { type: 'completed-search'; highSchools: highSchool[]; clusters: cluster[] }
  | { type: 'started-search' }
  | { type: 'change-location'; lat: number; lng: number }
  | { type: 'change-zoom'; zoom: number; lat: number; lng: number }
  | { type: 'error'; error: unknown }
  | { type: 'change-selected-highschool'; data?: highSchool }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'completed-search':
      return {
        ...state,
        highSchools: action.highSchools,
        clusters: action.clusters,
        isLoading: false,
      }
    case 'started-search':
      return {
        ...state,
        isLoading: true,
      }
    case 'error':
      return {
        ...state,
        isLoading: false,
      }
    case 'change-location':
      return {
        ...state,
        center: { lat: action.lat, lng: action.lng },
      }
    case 'change-zoom':
      return {
        ...state,
        zoom: action.zoom,
        center: { lat: action.lat, lng: action.lng },
      }
    case 'change-selected-highschool':
      return {
        ...state,
        selectedHighschool: action.data,
      }
  }
}

// TODO: commonize with parent component when we determine who owns api execution
const GOOGLE_CONSTANT = 156543.03392
const generateRadius = (lat: number, zoom: number, width = 1024, height = 1024) => {
  const METERS_PER_PX = (GOOGLE_CONSTANT * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom)
  return METERS_PER_PX * (Math.max(width, height) * 0.66)
}

const ExploreMap = () => {
  useTitle('Scoir - High Schools Map')

  const defaultCoords = useAppSelector((store) => {
    const collegeCoords = store.college.record?.Location?.coordinates
    if (collegeCoords) {
      return { lat: collegeCoords[1] as number, long: collegeCoords[0] as number }
    }
    return undefined
  })
  const { activeView, updateColumnFilters } = useViews()
  const selectedHandraiserClasses = getHandraiserClasses(activeView.columnConfigModel)
  const selectedFollowerClasses = getFollowerClasses(activeView.columnConfigModel)
  const selectedHistoricFollowerYears = getHistoricFollowerYears(activeView.columnConfigModel)
  const { toggleTargeted } = useTargetHighschoolsExplore()
  const history = useHistory()
  const persistedUrlState = decodeExploreMapStateParams(new URLSearchParams(history.location.search))

  const { lat: urlLat, lng: urlLng, zoom: urlZoom } = persistedUrlState
  let initLat = urlLat
  let initLng = urlLng
  if (initLat === undefined || initLng === undefined) {
    initLat = defaultCoords?.lat || 0
    initLng = defaultCoords?.long || 0
  }

  const [{ center, zoom, clusters, highSchools, selectedHighschool, isLoading }, dispatch] = useReducer(reducer, {
    ...initialState,

    center: { lat: initLat, lng: initLng },
    zoom: urlZoom || 7,
  })
  useEffect(() => {
    const params = encodeExploreMapState({
      lat: center.lat,
      lng: center.lng,
      zoom: zoom,
    })
    history.replace(history.location.pathname + '?' + params.toString())
  }, [history, center.lat, center.lng, zoom])

  useEffect(() => {
    let active = true
    const geoFilters = {
      lat: center.lat,
      long: center.lng,
      zoom: zoom,
      radius: generateRadius(center.lat, zoom, window.innerWidth, window.innerHeight),
    }
    dispatch({ type: 'started-search' })
    const normalizedLng = (((geoFilters.long % 360) + 540) % 360) - 180
    const query = createURLSeachParamsFromAPIFilters(
      createAPIFiltersFromUIFilters(activeView.filterModel, {
        handraiserClasses: [...getHandraiserClasses(activeView.columnConfigModel)],
        followerClasses: [...getFollowerClasses(activeView.columnConfigModel)],
        historicFollowerYears: [...getHistoricFollowerYears(activeView.columnConfigModel)],
      }),
    )
    query.set('limit', '1000')
    query.set('sort', 'distance')
    query.set('latitude', geoFilters.lat.toString())
    query.set('longitude', normalizedLng.toString())
    query.set('zoom', geoFilters.zoom.toString())
    query.set('radius', geoFilters.radius.toString())
    api
      .get<APIResult>(`/api/highschools/mapv2?${query.toString()}`)
      .then((result) => {
        if (!active) return
        dispatch({ type: 'completed-search', highSchools: result.records, clusters: result.clusters })
      })
      .catch((error) => {
        dispatch({ type: 'error', error })
      })
    return () => {
      active = false
    }
  }, [activeView.filterModel, activeView.columnConfigModel, center.lat, center.lng, zoom])

  const [filterOpen, setFilterOpen] = useState(false)

  const isPremiumExplorerEnabled = useAppSelector(
    (state) => state.college.record?.Features?.SupportsPremiumHighSchoolExplorer || false,
  )

  return (
    <FillViewHeight>
      <Box
        position="relative"
        height="100%"
      >
        <Box
          position="absolute"
          top={-2}
          right={0}
          zIndex={1}
          display="flex"
          alignItems="center"
          gridGap={4}
        >
          <Box width={300}>
            <PlaceFinder
              onChange={(location, isSchool) => {
                let newZoom = zoom
                if (isSchool) {
                  newZoom = 12
                } else if (zoom < 10) {
                  newZoom = 10
                }
                dispatch({ type: 'change-zoom', zoom: newZoom, lat: location.latitude, lng: location.longitude })
              }}
            />
          </Box>
          <Paper
            elevation={5}
            style={{ zIndex: 2 }}
          >
            <Box
              p={1}
              pt={2}
              display="flex"
              gridGap={4}
            >
              <Tooltip title="Filters">
                <Badge
                  color="primary"
                  badgeContent={getFilterCount(activeView.filterModel)}
                >
                  <Typography variant="srOnly">{getFilterCount(activeView.filterModel)} filters applied</Typography>
                  <IconButton
                    color="primary"
                    aria-label="view filters"
                    onClick={() => setFilterOpen(true)}
                  >
                    <FontAwesomeIcon
                      icon={faFilter}
                      size="sm"
                    />
                  </IconButton>
                </Badge>
              </Tooltip>
              <Box position="relative">
                <PremiumActionsWrapper
                  enabled={isPremiumExplorerEnabled}
                  trackingIdPlacement="saved-views"
                >
                  <ViewsButton
                    enabled={isPremiumExplorerEnabled}
                    iconOnly
                  />
                </PremiumActionsWrapper>
              </Box>
              <Tooltip title="View in Table">
                <IconButton
                  color="primary"
                  aria-label="view in table"
                  component={Link}
                  to={`${import.meta.env.BASE_URL}/recruit/highschools?${encodeToURLParams(
                    activeView.filterModel,
                    UIFilterConverters,
                  )}`}
                >
                  <FontAwesomeIcon
                    icon={faTableList}
                    size="sm"
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>
        </Box>
        <FilterDrawer
          isOpen={filterOpen}
          onClose={() => setFilterOpen(false)}
          onClickApply={(f) => {
            updateColumnFilters(f)
            setFilterOpen(false)
          }}
          defaultFilters={activeView.filterModel}
          showPremiumFilters={isPremiumExplorerEnabled}
          selectedHandraiserClasses={selectedHandraiserClasses}
          selectedFollowerClasses={selectedFollowerClasses}
          selectedHistoricFollowerYears={selectedHistoricFollowerYears}
        />
        <HighschoolPreviewDrawer
          open={selectedHighschool !== undefined}
          dataIsLoading={isLoading}
          onClickNext={() => undefined}
          onClickPrev={() => undefined}
          hasNext={false}
          hasPrevious={false}
          highschool={selectedHighschool}
          toggleTargeted={toggleTargeted}
          onClose={() => {
            dispatch({ type: 'change-selected-highschool', data: undefined })
          }}
        />
        <GoogleMap
          center={center}
          zoom={zoom}
          clusters={clusters}
          highSchools={highSchools}
          onSelectHighSchool={(hs) => {
            dispatch({ type: 'change-selected-highschool', data: hs })
          }}
          onCenterChange={(lat, lng) => dispatch({ type: 'change-location', lat, lng })}
          onZoomChange={(zoom, lat, lng) => {
            dispatch({ type: 'change-zoom', zoom, lat, lng })
          }}
        />
      </Box>
    </FillViewHeight>
  )
}

export default ExploreMap
